import React, { useState, useEffect } from 'react';
import { Users, Send } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { ExportCsvButton } from './ExportCsvButton';
import api from '../../lib/api';

const fmtDate = (d) => (d ? new Date(`${d.length === 7 ? `${d}-01` : d}T12:00:00`).toLocaleDateString('en-US', { month: 'long', day: d.length === 7 ? undefined : 'numeric', year: 'numeric' }) : 'Date unknown');

export const PastMeetingsAdmin = () => {
  const [meetings, setMeetings] = useState([]);
  const [inviting, setInviting] = useState('');

  const invite = async (eventId, title) => {
    if (!window.confirm(`Email everyone who RSVP'd to "${title}" an invitation to the next meeting?`)) return;
    setInviting(eventId);
    try {
      const res = await api.post(`/admin/past-meetings/${eventId}/invite`);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send invitations.');
    } finally {
      setInviting('');
    }
  };

  useEffect(() => {
    api.get('/admin/past-meetings').then((res) => setMeetings(res.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-xl text-[#3c4a2a]">Past Meetings & Their RSVPs</h2>
          <p className="text-sm text-[#3c4a2a]/60 mt-1">
            Everyone who showed interest in a past or removed meeting is kept here — a great follow-up list.
          </p>
        </div>
        <ExportCsvButton
          endpoint="/admin/rsvps"
          filename="wclc-all-rsvps.csv"
          testId="export-past-rsvps-btn"
          label="Download All RSVPs"
          columns={[
            { label: 'Event', key: 'event_title' },
            { label: 'Event ID', key: 'event_id' },
            { label: 'Name', key: 'name' },
            { label: 'Email', key: 'email' },
            { label: 'Guests', key: 'guests' },
            { label: 'RSVP Date', value: (r) => (r.created_at || '').slice(0, 10) },
          ]}
        />
      </div>

      {meetings.length === 0 ? (
        <p className="text-sm text-[#3c4a2a]/50" data-testid="past-meetings-empty">No past meetings with RSVPs yet.</p>
      ) : (
        <Accordion type="single" collapsible className="w-full" data-testid="past-meetings-list">
          {meetings.map((m) => (
            <AccordionItem key={m.event_id} value={m.event_id} className="border-b border-[#bd8cae]/30">
              <AccordionTrigger className="hover:no-underline py-4" data-testid={`past-meeting-${m.event_id}`}>
                <span className="flex flex-wrap items-center gap-3 text-left">
                  <span className="font-medium text-[#3c4a2a]">{m.title}</span>
                  <span className="text-sm text-[#3c4a2a]/50">{fmtDate(m.date)}</span>
                  {m.removed && <Badge className="bg-[#3c4a2a]/10 text-[#3c4a2a]/60">Removed from calendar</Badge>}
                  <Badge className="bg-[#bd8cae]/25 text-[#a3688f]">
                    <Users className="w-3 h-3 mr-1" />
                    {m.rsvps.length} interested
                  </Badge>
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <Button
                  size="sm"
                  onClick={() => invite(m.event_id, m.title)}
                  disabled={inviting === m.event_id}
                  data-testid={`invite-next-btn-${m.event_id}`}
                  className="mb-4 bg-[#bd8cae] hover:bg-[#a3688f] text-white"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {inviting === m.event_id ? 'Sending…' : 'Invite All to Next Meeting'}
                </Button>
                <div className="space-y-2 pb-3">
                  {m.rsvps.map((r, i) => (
                    <div key={`${r.email}-${i}`} className="flex flex-wrap items-center justify-between bg-[#f1e3eb]/50 rounded-lg px-4 py-2 text-sm">
                      <span className="font-medium text-[#3c4a2a]">{r.name}</span>
                      <a href={`mailto:${r.email}`} className="text-[#a3688f] hover:underline">{r.email}</a>
                      <span className="text-[#3c4a2a]/60">
                        {r.guests > 0 ? `+${r.guests} guest${r.guests > 1 ? 's' : ''} · ` : ''}
                        RSVP'd {(r.created_at || '').slice(0, 10)}
                      </span>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </div>
  );
};
